import React from 'react';
import { Link } from 'react-router-dom';
import {
  FiMail,
  FiPhone,
  FiMapPin,
  FiFacebook,
  FiTwitter,
  FiInstagram,
  FiLinkedin,
} from 'react-icons/fi';
import {
  COMPANY_INFO,
  CONTACT_INFO,
  SOCIAL_LINKS,
  NAV_LINKS,
} from '@utils/constants';

/**
 * Footer Component
 * Site footer with links, contact info, and social media
 */
const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Quick links
  const quickLinks = NAV_LINKS.filter(link => 
    ['/', '/about', '/products', '/services'].includes(link.path)
  );

  // Support links
  const supportLinks = NAV_LINKS.filter(link => 
    ['/membership', '/gallery', '/contact'].includes(link.path)
  );

  return (
    <footer className="bg-bukawa-primary-900 text-white">
      {/* Main Footer Content */}
      <div className="container-custom py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Company Info Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <img
                src="/assets/images/logo/bukawa-logo-white.png"
                alt="Bukawa ACE Ltd"
                className="h-16 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div style={{ display: 'none' }}>
                <div className="font-heading font-bold text-xl text-white">
                  {COMPANY_INFO.shortName}
                </div>
              </div>
            </Link>
            <p className="text-bukawa-gray-300 text-sm mb-4 leading-relaxed">
              {COMPANY_INFO.shortName} is a secondary cooperative organization
              serving over {COMPANY_INFO.totalMembers} farmers in Eastern Uganda
              through quality maize products and agricultural services.
            </p>
            <div className="flex items-center gap-4">
              {SOCIAL_LINKS.facebook !== '#' && (
                <a
                  href={SOCIAL_LINKS.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full bg-white/10 hover:bg-bukawa-secondary transition-colors"
                  aria-label="Facebook"
                >
                  <FiFacebook className="h-5 w-5" />
                </a>
              )}
              {SOCIAL_LINKS.twitter !== '#' && (
                <a
                  href={SOCIAL_LINKS.twitter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full bg-white/10 hover:bg-bukawa-secondary transition-colors"
                  aria-label="Twitter"
                >
                  <FiTwitter className="h-5 w-5" />
                </a>
              )}
              {SOCIAL_LINKS.instagram !== '#' && (
                <a
                  href={SOCIAL_LINKS.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full bg-white/10 hover:bg-bukawa-secondary transition-colors"
                  aria-label="Instagram"
                >
                  <FiInstagram className="h-5 w-5" />
                </a>
              )}
              {SOCIAL_LINKS.linkedin !== '#' && (
                <a
                  href={SOCIAL_LINKS.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full bg-white/10 hover:bg-bukawa-secondary transition-colors"
                  aria-label="LinkedIn"
                >
                  <FiLinkedin className="h-5 w-5" />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links Column */}
          <div>
            <h3 className="font-heading font-semibold text-lg mb-4">
              Quick Links
            </h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.id}>
                  <Link
                    to={link.path}
                    className="text-bukawa-gray-300 hover:text-bukawa-secondary transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links Column */}
          <div>
            <h3 className="font-heading font-semibold text-lg mb-4">
              Support
            </h3>
            <ul className="space-y-3">
              {supportLinks.map((link) => (
                <li key={link.id}>
                  <Link
                    to={link.path}
                    className="text-bukawa-gray-300 hover:text-bukawa-secondary transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/faq"
                  className="text-bukawa-gray-300 hover:text-bukawa-secondary transition-colors text-sm"
                >
                  FAQs
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info Column */}
          <div>
            <h3 className="font-heading font-semibold text-lg mb-4">
              Contact Us
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <FiMapPin className="h-5 w-5 text-bukawa-secondary flex-shrink-0 mt-0.5" />
                <span className="text-bukawa-gray-300 text-sm">
                  {CONTACT_INFO.location}
                </span>
              </li>
              <li className="flex items-start gap-3">
                <FiPhone className="h-5 w-5 text-bukawa-secondary flex-shrink-0 mt-0.5" />
                <div className="flex flex-col gap-1">
                  <a
                    href={`tel:${CONTACT_INFO.phone1}`}
                    className="text-bukawa-gray-300 hover:text-bukawa-secondary transition-colors text-sm"
                  >
                    {CONTACT_INFO.phone1}
                  </a>
                  <a
                    href={`tel:${CONTACT_INFO.phone2}`}
                    className="text-bukawa-gray-300 hover:text-bukawa-secondary transition-colors text-sm"
                  >
                    {CONTACT_INFO.phone2}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <FiMail className="h-5 w-5 text-bukawa-secondary flex-shrink-0 mt-0.5" />
                <a
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="text-bukawa-gray-300 hover:text-bukawa-secondary transition-colors text-sm break-all"
                >
                  {CONTACT_INFO.email}
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Copyright */}
            <p className="text-bukawa-gray-400 text-sm text-center md:text-left">
              © {currentYear} {COMPANY_INFO.shortName}. All rights reserved.
            </p>

            {/* Registration Info */}
            <p className="text-bukawa-gray-400 text-sm text-center md:text-right">
              Registered Cooperative No. {COMPANY_INFO.registrationNumber}
            </p>
          </div>
        </div>
      </div>

      {/* Certifications Badge (optional) */}
      <div className="bg-bukawa-primary-800 border-t border-white/10">
        <div className="container-custom py-4">
          <div className="flex flex-wrap items-center justify-center gap-6">
            <div className="flex items-center gap-2 text-bukawa-gray-400 text-xs">
              <span className="px-3 py-1 bg-white/10 rounded-full font-medium">
                UNBS Certified
              </span>
            </div>
            <div className="flex items-center gap-2 text-bukawa-gray-400 text-xs">
              <span className="px-3 py-1 bg-white/10 rounded-full font-medium">
                GMP Compliant
              </span>
            </div>
            <div className="flex items-center gap-2 text-bukawa-gray-400 text-xs">
              <span className="px-3 py-1 bg-white/10 rounded-full font-medium">
                Made in Uganda
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;